import React, { Fragment, useEffect } from 'react';
import { Confirmation, useConfirmation, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import Skeleton from 'react-loading-skeleton';
import { NoteType } from 'routes/screens/response/types';
import {
  AccountInfoType,
  AccountUsersResponseType,
  AllUsersType,
  UserType,
} from 'shared/types';
import { getAllUsersFromResponse } from 'shared/utils';
import {
  useDeleteNoteMutation,
  useGetAllNotes,
  useGetWorkflows,
} from '../../../../hooks';
import { NotesContainer } from '../../style';
import AddResponseNote from './add-response-note';
import ResponseNote from './note-display';

type workflowType = {
  workflow_id: string;
  name: string;
  type: string;
  deleted: boolean;
};

type AllNotestype = {
  surveyId: string;
  responseId: string;
  accountInfo?: AccountInfoType;
  users: AccountUsersResponseType;
  setIsEditNoteEnabled: (enabled: boolean) => void;
  userInfo: UserType;
};

const AllNotes = (props: AllNotestype) => {
  const {
    surveyId,
    responseId,
    users,
    accountInfo,
    setIsEditNoteEnabled,
    userInfo,
  } = props;
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const { ref: loadMoreBtnRef, inView } = useInView();
  const [deleteNoteId, setDeleteNoteId] = React.useState<string | null>(null);
  const [editNoteId, setEditNoteId] = React.useState<string | null>(null);
  const { setVisible, bindings } = useConfirmation(false);
  const [workflowPlaceholders, setWorkflowPlaceholders] =
    React.useState<any>(null);
  const allUsers: AllUsersType = getAllUsersFromResponse(users, 'display_id');

  const {
    data: allNotes,
    isFetching,
    isLoading,
    refetch: refetchAllNotes,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useGetAllNotes({
    surveyId,
    responseId,
  });
  const { mutate: deleteNote } = useDeleteNoteMutation({
    setToast,
    callback(success) {
      if (success) setDeleteNoteId(null);
    },
  });
  const { data: workflows = [], isFetching: isFetchingWorkflow } =
    useGetWorkflows();
  useEffect(() => {
    setIsEditNoteEnabled(Boolean(editNoteId !== null));
  }, [editNoteId]);

  useEffect(() => {
    if (workflows?.length) {
      const placeholders: any = {};
      workflows.forEach((workflow: workflowType) => {
        placeholders[`{{${workflow.workflow_id}}}`] = workflow;
      });
      setWorkflowPlaceholders(placeholders);
    }
  }, [workflows]);

  React.useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [inView]);

  React.useEffect(() => {
    refetchAllNotes();
  }, [allNotes]);

  return (
    <>
      {allNotes ? (
        <NotesContainer>
          {allNotes?.pages?.length
            ? allNotes?.pages?.map((page: any, index: number) => (
                <React.Fragment key={index}>
                  {page?.responseNotes?.map((note: NoteType) => (
                    <Fragment key={note.display_id}>
                      {editNoteId === note.display_id ? (
                        <AddResponseNote
                          notes={note.notes}
                          surveyId={surveyId}
                          responseId={responseId}
                          noteId={note}
                          inlineMode
                          setEditNoteId={setEditNoteId}
                        />
                      ) : (
                        <ResponseNote
                          note={note}
                          accountInfo={accountInfo}
                          allUsers={allUsers}
                          setDeleteNoteId={setDeleteNoteId}
                          toggleConfirmation={setVisible}
                          setEditNoteId={setEditNoteId}
                          workflowPlaceholders={workflowPlaceholders}
                          isFetchingWorkflow={isFetchingWorkflow}
                          userInfo={userInfo}
                        />
                      )}
                    </Fragment>
                  ))}
                </React.Fragment>
              ))
            : null}
          {isFetching && isLoading && !isFetchingNextPage ? (
            <div style={{ marginBottom: '10px' }}>
              <Skeleton width={575} height={75} />
            </div>
          ) : null}
          {isFetchingNextPage ? (
            <div style={{ marginBottom: '10px' }}>
              <Skeleton width={575} height={75} />
            </div>
          ) : hasNextPage ? (
            <button
              className="loadnew"
              ref={loadMoreBtnRef}
              onClick={() => fetchNextPage()}
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {t('load_more')}
            </button>
          ) : null}
        </NotesContainer>
      ) : null}
      <Confirmation
        {...bindings}
        width="550px"
        title={`${t('confirm_deletion')}`}
        cancelButtonText={`${t('cancel')}`}
        onConfirm={() => {
          deleteNote({
            deleteNoteId: deleteNoteId || '',
            surveyId,
            responseId,
          });
          setVisible(false);
        }}
        onDismiss={() => {
          setDeleteNoteId(null);
          setVisible(false);
        }}
        confirmButtonText={`${t('confirm')}`}
      >
        {t('forms.confirmation.confirm_delete_note')}
      </Confirmation>
    </>
  );
};

export default AllNotes;
